import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { queryClient } from "@/lib/queryClient"
import { apiRequest } from "@/lib/queryClient"
import { Customer, AssessmentTemplate } from "@/types"
import { useAuth } from "@/hooks/useAuth"
import { useLocation } from "wouter"

// Base schema for create mode - title will be auto-generated
const createAssessmentFormSchema = z.object({
  customerId: z.string().min(1, "Customer is required"),
  templateId: z.string().min(1, "Template is required"),
  alignmentEngineerId: z.string().min(1, "Please select an Alignment Engineer"),
  vcioId: z.string().min(1, "Please select a vCIO"),
  status: z.enum(["draft", "in_progress", "pending_review", "approved", "completed"]).default("draft"),
}).refine((data) => {
  // Ensure both roles are selected
  return data.alignmentEngineerId && data.vcioId;
}, {
  message: "Both Alignment Engineer and vCIO must be selected",
  path: ["vcioId"], // Show error on vcioId field
})

// Edit mode allows vcioId to be optional for backward compatibility
const editAssessmentFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  customerId: z.string().min(1, "Customer is required"),
  templateId: z.string().min(1, "Template is required"),
  alignmentEngineerId: z.string().min(1, "Please select an Alignment Engineer"),
  vcioId: z.string().optional(), // Optional for backward compatibility
  status: z.enum(["draft", "in_progress", "pending_review", "approved", "completed"]).default("draft"),
})

interface AssessmentFormProps {
  mode?: 'create' | 'edit'
  assessment?: any
  customers?: Customer[]
  templates?: AssessmentTemplate[]
  preselectedCustomerId?: string | null
  onSuccess?: () => void
  onCancel?: () => void
}

export default function AssessmentForm({ mode = 'create', assessment, customers, templates, preselectedCustomerId, onSuccess, onCancel }: AssessmentFormProps) {
  const { toast } = useToast()
  const { user } = useAuth()
  const [, setLocation] = useLocation()

  // Get current organization for organization ID
  const { data: organization } = useQuery<any>({
    queryKey: ['/api/organizations/current'],
    queryFn: () => apiRequest('GET', '/api/organizations/current'),
  })

  // Fetch customers if not provided
  const { data: fetchedCustomers } = useQuery<any>({
    queryKey: ["/api/customers"],
    enabled: !customers
  })

  // Fetch templates if not provided
  const { data: fetchedTemplates } = useQuery<any>({
    queryKey: ["/api/templates"],
    enabled: !templates
  })

  // Fetch users for role assignment
  const { data: users = [] } = useQuery<any[]>({
    queryKey: ["/api/users/list"],
  })

  // Use provided data or fetched data
  const customersData = customers || fetchedCustomers || []
  const templatesData = templates || fetchedTemplates || []

  // Filter users by role for dropdowns
  const vcioUsers = users.filter((u: any) => 
    ['vcio', 'admin', 'owner'].includes(u.role) || u.isOwner
  )
  
  const alignmentEngineerUsers = users.filter((u: any) => 
    ['alignment_engineer', 'vcio', 'admin', 'owner'].includes(u.role) || u.isOwner
  )

  const form = useForm({
    resolver: zodResolver(mode === 'edit' ? editAssessmentFormSchema : createAssessmentFormSchema),
    defaultValues: mode === 'edit' ? {
      title: assessment?.title || "",
      customerId: assessment?.customerId?.toString() || preselectedCustomerId || "",
      templateId: assessment?.templateId?.toString() || "",
      alignmentEngineerId: assessment?.assignedToId || user?.id || "",
      vcioId: assessment?.vcioId || "",
      status: assessment?.status || "draft" as const,
    } : {
      customerId: preselectedCustomerId || "",
      templateId: "",
      alignmentEngineerId: user?.id || "",
      vcioId: "",
      status: "draft" as const,
    },
  })

  const createAssessmentMutation = useMutation({
    mutationFn: async (data: any) => {
      // Auto-generate title from template and customer
      const selectedTemplate = templatesData.find((t: any) => t.id.toString() === data.templateId);
      const selectedCustomer = customersData.find((c: any) => c.id.toString() === data.customerId);
      const autoTitle = `${selectedTemplate?.name || 'Assessment'} - ${selectedCustomer?.name || 'Customer'}`;
      
      const payload = {
        title: autoTitle,
        customerId: parseInt(data.customerId),
        templateId: parseInt(data.templateId),
        status: data.status,
        assignedToId: data.alignmentEngineerId,
        vcioId: data.vcioId,
        organizationId: organization?.id,
      };
      const result = await apiRequest("POST", "/api/assessments", payload);
      return result;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/assessments"] })
      toast({
        title: "Success",
        description: "Assessment created successfully",
      })
      form.reset()
      onSuccess?.()
      // Navigate to the assessment detail page
      if (data && data.id) {
        setTimeout(() => {
          setLocation(`/assessments/${data.id}`)
        }, 100) // Small delay to ensure dialog closes first
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.message || "Failed to create assessment";
      const errorDetails = error?.details || "";
      
      toast({
        title: "Error Creating Assessment",
        description: errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage,
        variant: "destructive",
      });
      
      // Set form errors if specific field errors are returned
      if (error?.field === 'vcioId') {
        form.setError("vcioId", {
          type: "manual",
          message: errorMessage
        });
      } else if (error?.field === 'alignmentEngineerId') {
        form.setError("alignmentEngineerId", {
          type: "manual",
          message: errorMessage
        });
      }
    },
  })

  const editAssessmentMutation = useMutation({
    mutationFn: async (data: any) => {
      const payload = {
        title: data.title,
        customerId: parseInt(data.customerId),
        templateId: parseInt(data.templateId),
        assignedToId: data.alignmentEngineerId,
        vcioId: data.vcioId || null, // Allow null for backward compatibility
        status: data.status,
      };
      const result = await apiRequest("PATCH", `/api/assessments/${assessment.id}`, payload);
      return result;
    }, 
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/assessments"] })
      queryClient.invalidateQueries({ queryKey: ["/api/assessments", assessment.id] })
      toast({
        title: "Success",
        description: "Assessment updated successfully",
      })
      onSuccess?.()
    },
    onError: (error: any) => {
      const errorMessage = error?.message || "Failed to update assessment";
      const errorDetails = error?.details || "";
      
      toast({
        title: "Error Updating Assessment",
        description: errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage,
        variant: "destructive",
      });
      
      // Set form errors if specific field errors are returned
      if (error?.field === 'vcioId') {
        form.setError("vcioId", {
          type: "manual",
          message: errorMessage
        });
      } else if (error?.field === 'alignmentEngineerId') {
        form.setError("alignmentEngineerId", {
          type: "manual",
          message: errorMessage
        });
      }
    },
  })

  const onSubmit = (data: any) => {
    // Client-side role validation
    if (data.vcioId) {
      const vcioUser = users.find((u: any) => u.id === data.vcioId);
      if (vcioUser && !['vcio', 'admin', 'owner'].includes(vcioUser.role) && !vcioUser.isOwner) {
        toast({
          title: "Invalid vCIO Selection",
          description: "The selected user must have vCIO, Admin, or Owner role",
          variant: "destructive",
        });
        form.setError("vcioId", {
          type: "manual",
          message: "Selected user must have vCIO, Admin, or Owner role"
        });
        return;
      }
    }
    
    if (data.alignmentEngineerId) {
      const aeUser = users.find((u: any) => u.id === data.alignmentEngineerId);
      if (aeUser && !['alignment_engineer', 'vcio', 'admin', 'owner'].includes(aeUser.role) && !aeUser.isOwner) {
        toast({
          title: "Invalid Alignment Engineer Selection",
          description: "The selected user must have Alignment Engineer, vCIO, Admin, or Owner role",
          variant: "destructive",
        });
        form.setError("alignmentEngineerId", {
          type: "manual",
          message: "Selected user must have appropriate role"
        });
        return;
      }
    }
    
    if (mode === 'edit') {
      editAssessmentMutation.mutate(data);
    } else {
      createAssessmentMutation.mutate(data);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
        {mode === 'edit' && (
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Assessment Title</FormLabel>
                <FormControl>
                  <Input placeholder="Enter assessment title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="customerId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Customer</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a customer" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {customersData.map((customer: any) => (
                    <SelectItem key={customer.id} value={customer.id.toString()}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="templateId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Assessment Template</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {/* Organization Templates */}
                  {templatesData.filter((t: any) => !t.isGlobal).length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                        Organization Templates
                      </div>
                      {templatesData
                        .filter((t: any) => !t.isGlobal)
                        .map((template: any) => (
                          <SelectItem key={template.id} value={template.id.toString()}>
                            {template.name} ({template.category})
                          </SelectItem>
                        ))}
                    </>
                  )}
                  
                  {/* Global Templates */}
                  {templatesData.filter((t: any) => t.isGlobal).length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">
                        Global Templates
                      </div>
                      {templatesData
                        .filter((t: any) => t.isGlobal)
                        .map((template: any) => (
                          <SelectItem key={template.id} value={template.id.toString()}>
                            🌐 {template.name} ({template.category})
                          </SelectItem>
                        ))}
                    </>
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="alignmentEngineerId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Alignment Engineer</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an Alignment Engineer" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {alignmentEngineerUsers.map((u: any) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
                      {u.role && ` (${u.role})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="vcioId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                vCIO
                {mode === 'edit' && !assessment?.vcioId && (
                  <span className="text-xs text-muted-foreground ml-2">(Optional - can be added later)</span>
                )}
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a vCIO" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {vcioUsers.map((u: any) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
                      {u.role && ` (${u.role})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {mode === 'edit' && !assessment?.vcioId && (
                <p className="text-xs text-muted-foreground">
                  This assessment was created before vCIO assignment was available. You can optionally assign a vCIO now.
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2">
          {mode === 'edit' && onCancel && (
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
          {mode === 'create' && (
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
          <Button 
            type="submit" 
            disabled={createAssessmentMutation.isPending || editAssessmentMutation.isPending}
            className="bg-primary hover:bg-blue-700"
          >
            {mode === 'edit' 
              ? (editAssessmentMutation.isPending ? "Updating..." : "Update Assessment")
              : (createAssessmentMutation.isPending ? "Creating..." : "Create Assessment")
            }
          </Button>
        </div>
      </form>
    </Form>
  )
}
